import { apiService } from './api.service';
import {
  ApiResponse,
  SessionData,
  Ticket,
  TicketFields,
  TicketStats,
} from '../types';

export const sessionService = {
  getTicket(
    ticketId: string
  ): Promise<ApiResponse<{ ticketId: string; fields: TicketFields; ticket?: Ticket }>> {
    return apiService.getTicket(ticketId);
  },

  updateTicketFields(
    ticketId: string,
    fields: TicketFields
  ): Promise<ApiResponse<{ ticketId: string; fields: TicketFields; ticket?: Ticket }>> {
    return apiService.updateTicketFields(ticketId, fields);
  },

  getTicketHistory(
    userId: string
  ): Promise<ApiResponse<{ tickets: Ticket[]; total: number }>> {
    return apiService.getTicketHistory(userId);
  },

  updateTicketStatus(
    userId: string,
    ticketId: string,
    status: string,
    notes?: string
  ): Promise<ApiResponse<Ticket>> {
    return apiService.updateTicketStatus(userId, ticketId, status, notes);
  },

  getUserSession(
    userId: string
  ): Promise<ApiResponse<{ sessionData: SessionData }>> {
    return apiService.getUserSession(userId);
  },

  updateUserSession(
    userId: string,
    sessionData: SessionData
  ): Promise<ApiResponse<{ sessionData: SessionData }>> {
    return apiService.updateUserSession(userId, sessionData);
  },

  getTicketStats(userId: string): Promise<ApiResponse<TicketStats>> {
    return apiService.getTicketStats(userId);
  },

  searchTickets(
    userId: string,
    query?: string,
    filters?: any,
    page?: number,
    limit?: number
  ): Promise<ApiResponse<{
    tickets: Ticket[];
    total: number;
    page: number;
    totalPages: number;
  }>> {
    return apiService.searchTickets(userId, query, filters, page, limit);
  },

  getAppealSession(sessionId: string): Promise<any> {
    return apiService.getAppealSession(sessionId);
  },

  updateMitigationContext(
    sessionId: string,
    payload: { reasons: string[]; notes: Record<string, string> }
  ): Promise<any> {
    return apiService.updateMitigationContext(sessionId, payload);
  },

  updateAppealPath(
    sessionId: string,
    payload: { appealPath: 'AT_FAULT' | 'NOT_AT_FAULT' }
  ): Promise<any> {
    return apiService.updateAppealPath(sessionId, payload);
  },

  refreshWeatherContext(
    sessionId: string
  ): Promise<{ weather: any; reason?: string }> {
    return apiService.refreshWeatherContext(sessionId) as any;
  },

  generateAppealLetter(
    sessionId: string,
    payload?: { language?: string; currentLetter?: string; termsAccepted?: boolean }
  ): Promise<{ text: string; count: number; limit: number }> {
    return apiService.generateSessionAppeal(sessionId, payload) as any;
  },

  updateAppealLetter(
    sessionId: string,
    payload: { text: string }
  ): Promise<{ text: string }> {
    return apiService.updateSessionAppealLetter(sessionId, payload) as any;
  },

  updateExtraNote(
    sessionId: string,
    payload: { note: string }
  ): Promise<{ note: string }> {
    return apiService.updateSessionExtraNote(sessionId, payload) as any;
  },

  acceptSessionTerms(
    sessionId: string,
    payload: { version: string }
  ): Promise<{ termsAcceptedAt: number; termsAcceptedVersion: string }> {
    return apiService.acceptSessionTerms(sessionId, payload) as any;
  },

  acceptHumanReviewDisclaimer(
    sessionId: string,
    payload: { version: string }
  ): Promise<{ humanReviewDisclaimerAcceptedAt: number; humanReviewDisclaimerVersion: string }> {
    return apiService.acceptHumanReviewDisclaimer(sessionId, payload) as any;
  },

  createCheckoutSession(
    returnUrl: string,
    plan?: string
  ): Promise<{ url: string }> {
    return apiService.createCheckoutSession({ returnUrl, plan }) as any;
  },

  getBillingStatus(
    appealSessionId: string
  ): Promise<{ active: boolean; status?: string | null }> {
    return apiService.getBillingStatus(appealSessionId) as any;
  },

  getBillingMe(
    cacheBust?: boolean
  ): Promise<{
    active: boolean;
    status?: string | null;
    currentPeriodEnd?: any;
    capUsd?: number;
    costUsdTotal?: number;
    tokenUsageTotal?: number;
    isOverCap?: boolean;
    estimatedTokensCap?: number | null;
    planType?: string;
    subscriptionStatus?: string;
    humanReview?: { remaining: number; totalGranted: number; resetAt?: any | null };
    letterCredits?: { remaining: number; totalGranted: number };
    entitlements?: { canGenerateLetters: boolean; canUseHumanReview: boolean };
  }> {
    return apiService.getBillingMe(cacheBust) as any;
  },

  getUsageMe(): Promise<{
    limitCents: number;
    remainingCents: number;
    monthTotalCents: number;
    totalsByType: Record<string, number>;
    recentEvents: Array<{ id: string; type: string; costCentsEstimated: number; createdAt?: any }>;
  }> {
    return apiService.getUsageMe() as any;
  },

  useHumanReview(
    sessionId?: string
  ): Promise<{ remaining: number }> {
    return apiService.useHumanReview(sessionId) as any;
  },

  requestHumanReview(
    ticketId: string,
    payload: { appealLetter: string }
  ): Promise<{ requestId: string; remaining: number }> {
    return apiService.requestHumanReview(ticketId, payload) as any;
  },

  requestExperiencedReview(payload: {
    caseId: string;
    userEmail: string;
    letterVersionId?: string | null;
    letterText?: string | null;
  }): Promise<{ ok: boolean; requestId: string; status: string }> {
    return apiService.requestExperiencedReview(payload) as any;
  },

  getReviewRequest(caseId: string): Promise<{
    request: any | null;
    reviewState?: {
      canSubmit: boolean;
      disableReason: string;
      remaining: number;
      totalGranted: number;
      resetAt?: any | null;
      addonEligible?: boolean;
      submittedRecently?: boolean;
      requestStatus?: string | null;
      requestId?: string | null;
    };
  }> {
    return apiService.getReviewRequest(caseId) as any;
  },
};

export default sessionService;
